import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useSelectionStore } from "@/store/selectionStore";

const REFRESH_INTERVAL_MS = 30_000;

interface AutoRefreshState {
  /** Manual refresh: invalidates both list queries and resets the timer. */
  refresh: () => Promise<void>;
  /** True while a manual or background refetch is in flight. */
  isRefreshing: boolean;
  /**
   * False whenever auto-refresh is paused (detail drawer open or tab
   * hidden). Lets the UI reflect the paused state in tooltips.
   */
  isAutoRefreshActive: boolean;
}

/**
 * Polls the email + document list queries every 30s with two pause
 * conditions: the detail drawer is open, or the page is hidden.
 *
 * Manual refresh always works regardless of pause state — it explicitly
 * invalidates both list queries.
 */
export function useListAutoRefresh(): AutoRefreshState {
  const queryClient = useQueryClient();
  const selectedEmailId = useSelectionStore((s) => s.selectedEmailId);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  const refreshLockRef = useRef<boolean>(false);

  const doRefresh = useCallback(async () => {
    if (refreshLockRef.current) return;
    refreshLockRef.current = true;
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["emails"] }),
        queryClient.invalidateQueries({ queryKey: ["documents"] }),
        queryClient.invalidateQueries({ queryKey: ["list-counts"] }),
      ]);
    } finally {
      refreshLockRef.current = false;
      setIsRefreshing(false);
    }
  }, [queryClient]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibility = () =>
      setIsVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const isAutoRefreshActive = !selectedEmailId && isVisible;

  // Background polling — wired to the same callback as the manual button.
  useEffect(() => {
    if (!isAutoRefreshActive) return;
    const id = window.setInterval(() => {
      void doRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAutoRefreshActive, doRefresh]);

  return {
    refresh: doRefresh,
    isRefreshing,
    isAutoRefreshActive,
  };
}
